// src/api/authenticatedClient.js
import { supabase } from '../supabaseClient';
import config from '../config';
import { trackDebugEvent, trackAnalyticsEvent } from '../loggerService';

/**
 * Unified API client that automatically handles:
 *  - Supabase session tokens
 *  - Debug logging (requests, responses, durations, status codes, etc.)
 *  - Analytics events (e.g., endpoints called, durations, etc.)
 *  - Error handling (including network failures)
 *
 * @param {string} endpoint - API route to call (e.g., "/chataudio/openai").
 * @param {object} options - Additional fetch options (method, body, headers, etc.).
 *                           Pass skipBodyLogging:true for streaming endpoints.
 * @returns {Promise<Response>} The raw fetch response.
 */
export async function request(endpoint, options = {}) {
  // 1) Retrieve the session (for auth) via Supabase
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    trackDebugEvent('apiClient_session_error', { error: error.message });
    // Optionally, you might throw here
  }

  // 2) Merge default headers with any provided in options
  const defaultHeaders = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    defaultHeaders.Authorization = `Bearer ${session.access_token}`;
  }
  const headers = { ...defaultHeaders, ...options.headers };

  // 3) Convert body to JSON if needed
  let body = options.body;
  if (body && typeof body !== 'string') {
    body = JSON.stringify(body);
  }

  // 4) Debug & Analytics: Track before the request
  const startTime = performance.now();
  trackDebugEvent('apiClient_request_sent', { endpoint, body });
  trackAnalyticsEvent('api_request_initiated', { endpoint });

  // 5) Perform the fetch in a try/catch to handle network errors
  let response;
  try {
    response = await fetch(`${config.API_URL}${endpoint}`, {
      method: options.method || 'POST',
      headers,
      body,
      signal: options.signal,
    });
  } catch (networkError) {
    trackDebugEvent('apiClient_network_error', { endpoint, error: networkError.message });
    throw networkError;
  }

  // 6) Calculate duration and prepare for logging
  const duration = performance.now() - startTime;
  const status = response.status;
  let responseBody = null;

  // Only try to parse the response body if skipBodyLogging is not set.
  if (!options.skipBodyLogging) {
    try {
      responseBody = await response.clone().json();
    } catch (jsonError) {
      try {
        responseBody = await response.clone().text();
      } catch (textError) {
        responseBody = null;
      }
    }
  } else {
    responseBody = '[streaming response not logged]';
  }

  // 7) Debug event for the response
  trackDebugEvent('apiClient_request_received', {
    endpoint,
    status,
    duration,
    responseBody,
  });

  // 8) Analytics event for the completed API request
  trackAnalyticsEvent('api_request_completed', {
    endpoint,
    status,
    duration,
  });

  // Return the raw response so the caller can handle it (streaming intact if applicable)
  return response;
}
